import React, { useState } from "react";
import { toast } from "react-toastify";
import * as expenseService from "../../services/expenseService";
import { labelStyle, inputStyle } from "./expenseStyles";
import { STANDARD, TEMPLATE_OPTIONS, resolveTemplate } from "./expenseTemplates";
import { INVOICE_COLORS, DEFAULT_INVOICE_COLOR } from "./invoiceColors";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
export const CENTER_FORM_ID = "expense-center-form";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Mirrors the API: prefix (trailing - or _ dropped) + "-" + 2-digit sequence, or
// just the sequence when there is no prefix. Only the first number is shown.
export const previewInvoiceNumber = (prefix) => {
  const p = String(prefix || "").trim().toUpperCase().replace(/[-_]+$/, "");
  return p ? `${p}-01` : "01";
};

const emptyForm = {
  name: "",
  legal_entity_name: "",
  full_address: "",
  trn: "",
  invoice_prefix: "",
  upload_template: STANDARD,
  invoice_color: DEFAULT_INVOICE_COLOR,
};

// Rendered inside a SlidePanel; the panel footer's submit button targets this
// form via CENTER_FORM_ID. mode: "add" | "edit" | "view" (view = read-only).
function ExpenseCenterForm({ mode, center, onSaved, onSavingChange }) {
  const readOnly = mode === "view";
  const [form, setForm] = useState(
    center
      ? {
          name: center.name || "",
          legal_entity_name: center.legal_entity_name || "",
          full_address: center.full_address || "",
          trn: center.trn || "",
          invoice_prefix: center.invoice_prefix || "",
          upload_template: resolveTemplate(center.upload_template),
          invoice_color: center.invoice_color || DEFAULT_INVOICE_COLOR,
        }
      : emptyForm
  );
  // logo: undefined = unchanged, null = removed, "data:image..." = new upload
  const [logo, setLogo] = useState(undefined);
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const previewSrc = logo === undefined ? center?.logo_url || null : logo;

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be 2 MB or smaller");
      return;
    }
    try {
      setLogo(await readFileAsDataUrl(file));
    } catch (err) {
      toast.error("Could not read that image");
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (form.invoice_prefix && !/^[A-Za-z0-9_-]{1,20}$/.test(form.invoice_prefix.trim())) {
      errs.invoice_prefix = "Up to 20 letters, numbers, - or _";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (readOnly || !validate()) return;
    const payload = {
      name: form.name.trim(),
      legal_entity_name: form.legal_entity_name.trim(),
      full_address: form.full_address.trim(),
      trn: form.trn.trim(),
      invoice_prefix: form.invoice_prefix.trim(),
      upload_template: form.upload_template,
      invoice_color: form.invoice_color,
      ...(logo !== undefined ? { logo } : {}),
    };
    try {
      onSavingChange?.(true);
      const res =
        mode === "edit"
          ? await expenseService.updateCenter(center.id, payload)
          : await expenseService.createCenter(payload);
      if (res?.success) {
        toast.success(res.message || "Saved");
        onSaved();
      } else {
        toast.error(expenseService.responseMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      onSavingChange?.(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className="mb-3">
      <label htmlFor={`center-${key}`} style={labelStyle}>{label}</label>
      <input
        id={`center-${key}`}
        type="text"
        className={`form-control${errors[key] ? " is-invalid" : ""}`}
        style={inputStyle}
        value={form[key]}
        onChange={set(key)}
        disabled={readOnly}
        {...props}
      />
      {errors[key] && <div className="invalid-feedback">{errors[key]}</div>}
    </div>
  );

  return (
    <form id={CENTER_FORM_ID} onSubmit={onSubmit} noValidate>
      {field("name", "Name *", { placeholder: "e.g. Dream Star Car Polish", autoFocus: !readOnly })}
      {field("legal_entity_name", "Legal / Billing Entity Name", {
        placeholder: "Registered legal entity shown on the invoice",
      })}

      <div className="mb-3">
        <label htmlFor="center-logo" style={labelStyle}>Logo</label>
        {previewSrc ? (
          <div className="d-flex align-items-center gap-3 mb-2">
            <img
              src={previewSrc}
              alt="Logo"
              style={{ height: "56px", maxWidth: "160px", objectFit: "contain", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "4px" }}
            />
            {!readOnly && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLogo(null)}>
                Remove
              </button>
            )}
          </div>
        ) : readOnly ? (
          <div style={{ fontSize: "14px", color: "#6B7280" }}>No logo</div>
        ) : null}
        {!readOnly && (
          <input
            id="center-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="form-control"
            style={{ fontSize: "14px" }}
            onChange={onLogoChange}
          />
        )}
        {!readOnly && <div style={{ fontSize: "12px", color: "#6B7280", marginTop: 4 }}>PNG, JPG or WEBP, up to 2 MB.</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="center-full_address" style={labelStyle}>Full Address</label>
        <textarea
          id="center-full_address"
          className="form-control"
          rows={3}
          style={{ fontSize: "14px", borderRadius: "6px" }}
          value={form.full_address}
          onChange={set("full_address")}
          disabled={readOnly}
          placeholder="Street, area, city, country"
        />
      </div>

      {field("trn", "TRN (Tax Registration Number)", { placeholder: "e.g. 104323551200003" })}
      <div className="mb-3">
        <label htmlFor="center-upload_template" style={labelStyle}>Upload template</label>
        <select
          id="center-upload_template"
          className="form-select"
          style={inputStyle}
          value={form.upload_template}
          onChange={set("upload_template")}
          disabled={readOnly}
        >
          {TEMPLATE_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <div style={{ fontSize: "12px", color: "#6B7280", marginTop: 4 }}>
          The spreadsheet layout this branch uploads. It also sets the columns shown for its jobs and printed on its invoices.
        </div>
      </div>
      {field("invoice_prefix", "Invoice No format (optional)", { placeholder: "e.g. DSP", maxLength: 20 })}
      <div style={{ fontSize: "12px", color: "#6B7280", marginTop: -8 }}>
        First invoice number: <strong>{previewInvoiceNumber(form.invoice_prefix)}</strong>. A hyphen and a 2-digit sequence
        (01, 02, 03 ...) follow your prefix. Leave it blank for plain numbers with no prefix.
      </div>

      <div className="mt-3 mb-3">
        <label style={labelStyle} id="center-invoice-color-label">Invoice colour</label>
        <div role="radiogroup" aria-labelledby="center-invoice-color-label" className="d-flex flex-wrap gap-2">
          {INVOICE_COLORS.map((c) => {
            const active = form.invoice_color === c.value;
            return (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={c.label}
                title={c.label}
                disabled={readOnly}
                onClick={() => setForm((f) => ({ ...f, invoice_color: c.value }))}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: c.value,
                  border: "2px solid #fff",
                  boxShadow: active ? `0 0 0 2px ${c.value}` : "0 0 0 1px #D1D5DB",
                  cursor: readOnly ? "default" : "pointer",
                  padding: 0,
                }}
              />
            );
          })}
        </div>
        <div style={{ fontSize: "12px", color: "#6B7280", marginTop: 6 }}>
          Used for the table header and totals bar on this branch's invoices. Invoices already generated keep their colour.
        </div>
      </div>
    </form>
  );
}

export default ExpenseCenterForm;
